import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import Layout from 'components/common/layout/Layout';
import SearchFilter from 'components/common/forms/SearchFilter';
import SingleBtn from 'components/common/elements/SingleBtn';
import ScoreManagementTable from './ScoreManagementTable';
import Paging from 'components/common/nav/Paging';
import { DEFAULT_PAGE_SIZE } from 'constants/page';
import { SortDirection } from 'types/apis/common';
import { ScoreInfoSortBy, ScoreInfoSortByFormLabel } from 'types/apis/score';
import { getScoreList, postScore } from 'apis/score';
import { isTCustomAxiosError } from 'apis/config/axios';
import { getToday } from 'utils/date';
import useModal from 'hooks/useModal';

const ScoreManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const { openModal } = useModal();

  const [page, setPage] = useState(Number(query.get('page')) || 1);
  const [params, setParams] = useState({
    page,
    size: DEFAULT_PAGE_SIZE,
    courseInfoId: Number(query.get('courseInfoId')) || undefined,
    chartRound: query.get('chartRound') || undefined,
    searchWord: query.get('searchWord') || undefined,
    isNotEvaluated: query.get('isNotEvaluated') === 'true' || false,
    sortBy: (query.get('sortBy') as ScoreInfoSortBy) || undefined,
    isAsc:
      query.get('sort') === SortDirection.Asc
        ? true
        : query.get('sort') === SortDirection.Desc
        ? false
        : undefined,
  });
  const [sortBy, setSortBy] = useState(query.get('sortBy') || '');
  const [sortDirection, setSortDirection] = useState(query.get('sort') || '');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (sortBy !== query.get('sortBy')) setSortBy('');
    if (sortDirection !== query.get('sort')) setSortDirection('');
    setParams({
      ...params,
      page,
      courseInfoId: Number(query.get('courseInfoId')) || undefined,
      chartRound: query.get('chartRound') || undefined,
      searchWord: query.get('searchWord') || undefined,
      isNotEvaluated: query.get('isNotEvaluated') === 'true' || false,
    });
  }, [location.search, page]);

  const { data: getScoreListData, isFetching } = useQuery(
    ['getScoreList', params],
    () => getScoreList(params),
    {
      enabled: !!params.courseInfoId && !!params.chartRound,
    }
  );

  const chartClosingStatus =
    getScoreListData?.data.chartClosingStatus || undefined;
  const scoreListData = getScoreListData?.data.studentScoreList || [];
  const scoreListPaging = getScoreListData?.data.paging;

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const onClickSort = (label: string) => {
    const sortAsc =
      sortDirection === ''
        ? SortDirection.Asc
        : sortDirection === SortDirection.Asc
        ? SortDirection.Desc
        : '';
    const sortBy = {
      [ScoreInfoSortByFormLabel.Score1]: ScoreInfoSortBy.Score1,
      [ScoreInfoSortByFormLabel.Score2]: ScoreInfoSortBy.Score2,
    }[label as ScoreInfoSortByFormLabel];

    setSortDirection(sortAsc);
    setSortBy(sortBy);

    if (page !== 1) {
      setPage(1);
    }
    if (!sortAsc) {
      query.delete('sort');
      query.delete('sortBy');
    } else {
      query.set('sort', sortAsc);
      query.set('sortBy', sortBy);
    }
    navigate({
      search: query.toString(),
    });

    setParams({
      ...params,
      page: 1,
      sortBy: (query.get('sortBy') as ScoreInfoSortBy) || undefined,
      isAsc:
        sortAsc === SortDirection.Asc
          ? true
          : sortAsc === SortDirection.Desc
          ? false
          : undefined,
    });
  };

  const { mutate: postScoreMutation } = useMutation(
    'postScore',
    () =>
      postScore({
        courseInfoId: Number(params.courseInfoId),
        chartRound: Number(params.chartRound),
      }),
    {
      onSuccess: () =>
        openModal({
          title: '성적 등록이 완료되었습니다',
          text: `${getToday('yyyy년 mm월 dd일')}에 ${
            chartClosingStatus?.firstChartScoreDisclosure &&
            !!chartClosingStatus?.secondSubmitStartDate
              ? 2
              : 1
          }차 성적 등록을 완료하셨습니다.`,
        }),
      onError: (error) => {
        const code = isTCustomAxiosError(error) && error.code;
        const message = isTCustomAxiosError(error) && error.message;

        openModal({
          title: code !== 409 ? '성적 등록에 실패했습니다.' : '',
          text:
            code === 409
              ? '이미 성적 처리가 완료된 회차입니다.'
              : message || '확인 후 다시 시도해 주세요.',
        });
      },
    }
  );

  const handleSendScore = () => {
    if (
      chartClosingStatus?.firstChartScoreDisclosure &&
      (!chartClosingStatus?.secondSubmitStartDate ||
        chartClosingStatus?.secondChartScoreDisclosure)
    ) {
      openModal({
        text: '이미 성적 처리가 완료된 회차입니다.',
      });
      return;
    }
    openModal({
      isConfirm: true,
      title: `${
        chartClosingStatus?.firstChartScoreDisclosure &&
        !!chartClosingStatus?.secondSubmitStartDate
          ? 2
          : 1
      }차 성적을 마감하시겠습니까?`,
      text: '성적 마감한 경우, 학생들의 성적을 입력 전 상태로 되돌릴 수 없습니다. 만약 성적 수정을 원하신다면 성적 수정하기 기능을 사용하여 수정해 주세요.',
      onSubmit: () => postScoreMutation(),
    });
  };

  return (
    <Layout>
      <h1 className="main_title mt40 mb24">성적관리</h1>

      <SearchFilter
        dropdown={[
          {
            id: 'course',
            placeholder: '강의명',
            width: '204px',
          },
          {
            id: 'courseInfo',
            placeholder: '분반',
            width: '188px',
          },
          {
            id: 'chartRound',
            placeholder: '(회차) 회차명',
            width: '188px',
          },
        ]}
        input={{
          placeholder: '이름, 학번, 연락처를 입력해 주세요.',
          width: '300px',
        }}
        checkbox={[
          {
            id: 'isNotEvaluated',
            label: '성적 미등록 학생 보기',
          },
        ]}
        childrenPosition="bottom"
        onChange={() => setPage(1)}
      >
        <SingleBtn
          btnText="성적 마감하기"
          hasActiveOutline={true}
          btnStyle={{
            padding: '8px 16px',
          }}
          disabled={!params.courseInfoId || !params.chartRound}
          onClick={handleSendScore}
        />
      </SearchFilter>

      <ScoreManagementTable
        params={{
          courseInfoId: Number(params.courseInfoId),
          chartRound: Number(params.chartRound),
        }}
        data={scoreListData}
        paging={scoreListPaging}
        sortBy={
          sortBy === 'score1'
            ? 'firstEvaluationAvgScore'
            : sortBy === 'score2'
            ? 'secondEvaluationAvgScore'
            : ''
        }
        sortDirection={sortDirection}
        onClickSort={onClickSort}
        isLoading={isLoading}
      />

      {scoreListPaging && scoreListPaging.totalElements > 0 && (
        <div className="mt32">
          <Paging
            activePage={page}
            totalItemsCount={scoreListPaging.totalElements}
            handlePageChange={setPage}
          />
        </div>
      )}
    </Layout>
  );
};
export default ScoreManagement;
